import { Directive, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { PrepTourAnchorRegistry } from './tour-anchor-registry.service';
import { anchorId } from './tour.types';

/**
 * Registers the host element under a stable id so guide steps can target it:
 *
 * ```html
 * <button [prepTourAnchor]="anchors.dashboard.add">…</button>
 * ```
 *
 * Registration follows the element's lifetime and re-registers when the id
 * changes; an empty id unregisters (useful for "the focused row" anchors
 * that move between elements).
 */
@Directive({ selector: '[prepTourAnchor]' })
export class PrepTourAnchorDirective implements OnInit, OnChanges, OnDestroy {
  @Input('prepTourAnchor') id: string | null | undefined;
  @Input() prepTourAnchorPadding = 6;

  private unregister: (() => void) | null = null;

  constructor(private readonly el: ElementRef<HTMLElement>, private readonly registry: PrepTourAnchorRegistry) {}

  ngOnInit(): void {
    this.sync();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['id'] && !changes['id'].firstChange) this.sync();
    else if (changes['prepTourAnchorPadding'] && !changes['prepTourAnchorPadding'].firstChange) this.sync();
  }

  ngOnDestroy(): void {
    this.release();
  }

  private sync(): void {
    this.release();
    if (!this.id) return;
    this.unregister = this.registry.register(anchorId(this.id), this.el.nativeElement, this.prepTourAnchorPadding);
  }

  private release(): void {
    if (this.unregister) {
      this.unregister();
      this.unregister = null;
    }
  }
}
